import { useTranslation } from 'react-i18next';
import { StatusPill } from '@ui-kit';
import type { RoutingStatusResponse } from '../types/tenantSwitcher';

interface RoutingStatusCardProps {
  data: RoutingStatusResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

function DataSourceBadge({ type }: { type: 'READ' | 'WRITE' }) {
  return (
    <StatusPill tone={type === 'READ' ? 'info' : 'success'}>{type}</StatusPill>
  );
}

function SkeletonLine({ width = '60%' }: { width?: string }) {
  return (
    <div
      style={{
        height: 14,
        width,
        borderRadius: 'var(--radius-sm)',
        background: 'var(--color-surface-elevated)',
        animation: 'pulse 1.5s ease-in-out infinite',
        marginBottom: 'var(--spacing-2)',
      }}
    />
  );
}

export function RoutingStatusCard({ data, isLoading, isError, onRetry }: RoutingStatusCardProps) {
  const { t } = useTranslation();

  return (
    <div
      style={{
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--color-surface-background)',
        padding: 'var(--spacing-4)',
        minWidth: 240,
      }}
      aria-label={t('tenantSwitcher.routing.title')}
    >
      {/* 제목 */}
      <div
        style={{
          fontWeight: 700,
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-3)',
          borderBottom: '1px solid var(--color-surface-border)',
          paddingBottom: 'var(--spacing-2)',
        }}
      >
        {t('tenantSwitcher.routing.title')}
      </div>

      {/* Loading */}
      {isLoading && (
        <div aria-label={t('tenantSwitcher.routing.loadingAriaLabel')} aria-busy="true">
          <SkeletonLine width="80%" />
          <SkeletonLine width="60%" />
          <SkeletonLine width="70%" />
          <SkeletonLine width="90%" />
          <SkeletonLine width="75%" />
        </div>
      )}

      {/* Error */}
      {isError && !isLoading && (
        <div style={{ textAlign: 'center', padding: 'var(--spacing-3)' }}>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', marginBottom: 'var(--spacing-2)' }}>
            {t('tenantSwitcher.error.loadFailed')}
          </p>
          <button
            type="button"
            onClick={onRetry}
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-brand-primary)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('tenantSwitcher.error.retry')}
          </button>
        </div>
      )}

      {/* Default */}
      {data && !isLoading && (
        <dl style={{ margin: 0, display: 'grid', gap: 'var(--spacing-2)' }}>
          {/* 라우팅 활성 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <dt style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 600 }}>
              {t('tenantSwitcher.routing.enabled')}
            </dt>
            <dd style={{ margin: 0 }}>
              <StatusPill tone={data.routingEnabled ? 'success' : 'warning'}>
                {data.routingEnabled ? t('tenantSwitcher.routing.on') : t('tenantSwitcher.routing.off')}
              </StatusPill>
            </dd>
          </div>

          {/* 조회 → READ */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <dt style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 600 }}>
              {t('tenantSwitcher.routing.read')}
            </dt>
            <dd style={{ margin: 0 }}>
              <DataSourceBadge type={data.currentReadType} />
            </dd>
          </div>

          {/* 변경 → WRITE */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <dt style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 600 }}>
              {t('tenantSwitcher.routing.write')}
            </dt>
            <dd style={{ margin: 0 }}>
              <DataSourceBadge type={data.currentWriteType} />
            </dd>
          </div>

          {/* WRITE 노드 */}
          {data.writeNodeMasked && (
            <div style={{ paddingTop: 'var(--spacing-1)' }}>
              <dt style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 2 }}>
                {t('tenantSwitcher.routing.writeNode')}
              </dt>
              <dd
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)',
                  wordBreak: 'break-all',
                }}
              >
                {data.writeNodeMasked}
              </dd>
            </div>
          )}

          {/* READ 노드 */}
          {data.readNodeMasked && (
            <div>
              <dt style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 2 }}>
                {t('tenantSwitcher.routing.readNode')}
              </dt>
              <dd
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)',
                  wordBreak: 'break-all',
                }}
              >
                {data.readNodeMasked}
              </dd>
            </div>
          )}

          {/* 안내 메모 */}
          {data.note && (
            <div
              style={{
                marginTop: 'var(--spacing-2)',
                padding: 'var(--spacing-2) var(--spacing-3)',
                background: 'var(--color-surface-muted)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
                borderLeft: '3px solid var(--color-warning)',
              }}
            >
              {data.note}
            </div>
          )}
        </dl>
      )}
    </div>
  );
}
